import { useState, useCallback, useEffect } from 'react';

/**
 * Get feed by ID from feeds object
 * Handles both direct key lookup (HOME) and id field matching (home)
 */
function getFeedById(feedId, FEEDS) {
  if (!FEEDS || !feedId) return null;
  
  // Try direct key lookup first (uppercase key like 'HOME')
  const upperKey = feedId.toUpperCase();
  if (FEEDS[upperKey]) {
    return FEEDS[upperKey];
  }
  
  // Try lowercase key
  const lowerKey = feedId.toLowerCase();
  if (FEEDS[lowerKey]) {
    return FEEDS[lowerKey];
  }
  
  // Try exact match
  if (FEEDS[feedId]) {
    return FEEDS[feedId];
  }
  
  // Fallback: search by id field
  const feedKeys = Object.keys(FEEDS);
  for (const key of feedKeys) {
    if (FEEDS[key].id === feedId) {
      return FEEDS[key];
    }
  }
  
  return null;
}

/**
 * useFeedNavigation Hook
 * 
 * Manages nested feed navigation with stack-based history
 * Preserves active index per feed for position restoration
 * 
 * Stack entry: { feedId, activeIndex }
 * 
 * Navigation flow:
 * - openChildFeed(feedId) → push new feed to stack
 * - goBack() → pop stack, restore parent feed + index
 * - setActiveIndex(index) → update current feed's active post
 */
export default function useFeedNavigation(initialFeedId = 'home', FEEDS = {}) {
  // Navigation stack: [{ feedId, activeIndex }, ...]
  const [navigationStack, setNavigationStack] = useState([
    { feedId: initialFeedId, activeIndex: 0 }
  ]);
  
  // Recompute current feed when FEEDS or stack changes
  const [currentFeed, setCurrentFeed] = useState(null);
  
  useEffect(() => {
    const currentEntry = navigationStack[navigationStack.length - 1];
    const feed = getFeedById(currentEntry.feedId, FEEDS);
    setCurrentFeed(feed);
  }, [navigationStack, FEEDS]);

  // Get current (top of stack)
  const currentEntry = navigationStack[navigationStack.length - 1];
  const currentFeedId = currentEntry.feedId;
  const currentIndex = currentEntry.activeIndex;

  /**
   * Update active index for current feed
   */
  const setActiveIndex = useCallback((newIndex) => {
    setNavigationStack(prevStack => {
      const newStack = [...prevStack];
      newStack[newStack.length - 1] = {
        ...newStack[newStack.length - 1],
        activeIndex: newIndex
      };
      return newStack;
    });
  }, []);

  /**
   * Navigate to next post (with loop support)
   */
  const navigateNext = useCallback(() => {
    if (!currentFeed) return;
    
    const totalPosts = currentFeed.posts.length;
    const nextIndex = currentIndex + 1;
    
    if (nextIndex >= totalPosts) {
      // End of feed - loop if enabled
      if (currentFeed.loopEnabled) {
        setActiveIndex(0);
      }
    } else {
      setActiveIndex(nextIndex);
    }
  }, [currentFeed, currentIndex, setActiveIndex]);

  /**
   * Navigate to previous post (with loop support)
   */
  const navigatePrev = useCallback(() => {
    if (!currentFeed) return;
    
    const totalPosts = currentFeed.posts.length;
    const prevIndex = currentIndex - 1;
    
    if (prevIndex < 0) {
      // Start of feed - loop if enabled
      if (currentFeed.loopEnabled) {
        setActiveIndex(totalPosts - 1);
      }
    } else {
      setActiveIndex(prevIndex);
    }
  }, [currentFeed, currentIndex, setActiveIndex]);

  /**
   * Open child feed
   * Pushes new feed to stack, starting at index 0
   */
  const openChildFeed = useCallback((childFeedId) => {
    const childFeed = getFeedById(childFeedId, FEEDS);
    
    if (!childFeed) {
      console.warn(`Feed not found: ${childFeedId}`);
      return;
    }

    setNavigationStack(prevStack => [
      ...prevStack,
      { feedId: childFeedId, activeIndex: 0 }
    ]);
  }, [FEEDS]);

  /**
   * Pop every child feed and return to the root feed in one state update
   */
  const resetToRoot = useCallback(() => {
    setNavigationStack(prevStack => prevStack.length > 1 ? [prevStack[0]] : prevStack);
  }, []);

  /**
   * Go back to parent feed
   * Pops stack, restores previous feed + exact index
   */
  const goBack = useCallback(() => {
    if (navigationStack.length <= 1) {
      // Already at root, cannot go back
      return;
    }

    setNavigationStack(prevStack => prevStack.slice(0, -1));
  }, [navigationStack.length]);

  /**
   * Check if we can go back
   */
  const canGoBack = navigationStack.length > 1;

  /**
   * Get parent feed info (for back button label)
   */
  const parentFeed = navigationStack.length > 1 
    ? getFeedById(navigationStack[navigationStack.length - 2].feedId, FEEDS)
    : null;

  return {
    // Current state
    currentFeed,
    currentFeedId,
    currentIndex,
    totalPosts: currentFeed?.posts.length || 0,
    
    // Navigation actions
    setActiveIndex,
    navigateNext,
    navigatePrev,
    openChildFeed,
    goBack,
    resetToRoot,
    
    // State checks
    canGoBack,
    parentFeed,
    isAtStart: currentIndex === 0,
    isAtEnd: currentFeed ? currentIndex === currentFeed.posts.length - 1 : false,
    
    // Advanced
    navigationStack,
    stackDepth: navigationStack.length
  };
}
